/**
 * Who is signed in, and making sure they have a household.
 *
 * Static Web Apps does the authentication; this module only *reads* the result
 * of it. There is no token handling here, no password, no refresh, no storage
 * of anything identifying — `/.auth/me` is asked on load and the answer is held
 * in memory for the life of the page. Signing out is a link.
 *
 * THE HOUSEHOLD, NOT THE USER (§9.4). One household, two people, four
 * applications over six years. The sponsor usually signs up first and the
 * applicant follows, so the account creates a household and joins the person to
 * it — never a bare user that would have to be migrated later. `createHousehold`
 * is idempotent and answers `created: false` for someone who already has one,
 * so calling it on every sign-in is correct rather than merely tolerable.
 */
import { clear, recall, type Handoff } from "@verity/handoff";

export interface Principal {
  userId: string;
  userDetails: string;
  identityProvider: string;
}

export interface Session {
  principal: Principal;
  householdId: string;
  /** True only on the sign-in that created the household. */
  isNew: boolean;
  /** True when a check carried in from the calculator became a case (F2). */
  caseStarted: boolean;
}

export const LOGIN_URL = "/.auth/login/verity";
export const LOGOUT_URL = "/.auth/logout";

/**
 * Sign in and come back to where you were.
 *
 * WHY THIS IS NOT JUST `LOGIN_URL`. `/app/*` used to carry Static Web Apps'
 * `allowedRoles: ["authenticated"]`, which answers 401, which our
 * `responseOverrides` turned into a redirect to a *static* login URL. A static
 * URL cannot name the page you were asking for, and SWA's login sends you to
 * `/` when nobody names one — so every arrival at `/app/anything` while signed
 * out silently became an arrival at the marketing home page.
 *
 * That is what happened coming back from Stripe: the return URL was
 * `/app/#billing` and the person landed on `/`. The destination was not lost by
 * accident, it was structurally unrecoverable — by the time the browser follows
 * the 302 the original URL is gone.
 *
 * So the gate moved into the page, where the destination is still in hand. The
 * bundle it used to protect is HTML, JavaScript, CSS and three fonts; every
 * fact about a case comes from `/api/*`, which resolves the principal from the
 * SWA header and reads authorisation from the database (`apps/api/src/auth.ts`).
 * The gate was never the boundary. See ADR-0052.
 *
 * RELATIVE ONLY, AND ENFORCED HERE. `post_login_redirect_uri` is a redirect
 * target that Static Web Apps honours, which makes it an open-redirect sink —
 * the classic phishing shape being a link to OUR sign-in page that lands on
 * somebody else's. The destination is taken from the page we are already on, so
 * there is no caller-supplied value to abuse today; the normalising below is
 * what keeps that true if this ever grows an argument.
 *
 * `//host/path` is the one shape that escapes: a browser reads it as
 * protocol-relative and leaves the origin. `window.location.pathname` never
 * produces one — but "never produces one" is a fact about a browser, and this
 * is a redirect.
 */
export function loginUrlForHere(location: { pathname: string; search: string; hash: string }): string {
  const path = `/${location.pathname.replace(/^\/+/, "")}`;
  const here = `${path}${location.search}${location.hash}`;
  return `${LOGIN_URL}?post_login_redirect_uri=${encodeURIComponent(here)}`;
}

interface ClientPrincipalResponse {
  clientPrincipal: (Principal & { userRoles?: string[] }) | null;
}

/** null means "not signed in", which is a normal state and not an error. */
export async function whoami(fetchImpl: typeof fetch = fetch): Promise<Principal | null> {
  let response: Response;
  try {
    response = await fetchImpl("/.auth/me", { headers: { accept: "application/json" } });
  } catch {
    return null;
  }
  if (!response.ok) return null;

  let body: ClientPrincipalResponse;
  try {
    body = (await response.json()) as ClientPrincipalResponse;
  } catch {
    return null;
  }

  const p = body.clientPrincipal;
  // A principal without the `authenticated` role is not signed in — the same
  // condition apps/api/src/auth.ts applies server-side. Agreeing with the API
  // here means the page never renders a signed-in shell around requests the
  // API is about to refuse.
  if (!p?.userId || !p.userRoles?.includes("authenticated")) return null;

  return {
    userId: p.userId,
    userDetails: p.userDetails,
    identityProvider: p.identityProvider,
  };
}

/**
 * Establish the household and, if a check came with them, turn it into a case.
 *
 * TWO STEPS, NOT ONE, and deliberately. Creating a household is about identity;
 * starting a case is about the Immigration Rules. Folding them together would
 * mean somebody who signs up without checking first has no way to get a case
 * later without pretending to create a second household — which is F6's whole
 * journey.
 *
 * The handoff is CLEARED once presented, whether or not it worked. A stash that
 * failed will fail again — the token is spent, or the answers are in a shape the
 * server no longer understands — and retrying it on every sign-in turns one
 * silent miss into a permanent one.
 */
export async function establishHousehold(
  principal: Principal,
  now: Date,
  fetchImpl: typeof fetch = fetch,
): Promise<Session | null> {
  const handoff: Handoff | null = recall(now);

  let response: Response;
  try {
    response = await fetchImpl("/api/households", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ role: "sponsor", save_token: handoff?.token ?? null }),
    });
  } catch {
    return null;
  }
  if (!response.ok) return null;

  let body: {
    household_id?: string;
    created?: boolean;
    result_id?: string | null;
  };
  try {
    body = (await response.json()) as typeof body;
  } catch {
    return null;
  }
  if (!body.household_id) return null;

  const householdId = body.household_id;
  let caseStarted = false;

  if (handoff) {
    caseStarted = await startCase(handoff, fetchImpl);
    // Spent either way. See above.
    clear();
  }

  return {
    principal,
    householdId,
    isNew: body.created === true,
    caseStarted,
  };
}

/**
 * Turn a carried check into a case.
 *
 * The answers go to the server and the server recomputes the determination —
 * it is never sent one. A browser that reported its own verdict could report a
 * favourable one, which is the same reason `saveResult` recomputes.
 *
 * Failure is not fatal and must not be. Somebody whose stash could not be
 * replayed still has an account; they are asked for their answers again (F6),
 * which is a worse experience than we intended and a much better one than an
 * error page on the first screen they ever see.
 */
async function startCase(handoff: Handoff, fetchImpl: typeof fetch): Promise<boolean> {
  const body = handoff.answers
    ? { answers: handoff.answers }
    : handoff.token
      ? { save_token: handoff.token }
      : null;
  if (!body) return false;

  try {
    const response = await fetchImpl("/api/cases", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    return response.ok;
  } catch {
    return false;
  }
}
